"use client"

import { useState } from "react"
import { Search, Filter, X } from "lucide-react"

interface ResourcesFiltersProps {
  onFilterChange?: (filters: ResourceFilterState) => void
}

export interface ResourceFilterState {
  searchQuery: string
  resourceType: string
  targetClass: string
}

export function ResourcesFilters({ onFilterChange }: ResourcesFiltersProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [resourceType, setResourceType] = useState("")
  const [targetClass, setTargetClass] = useState("")
  const [showFilters, setShowFilters] = useState(false)

  const handleFilterChange = (newSearchQuery?: string, newResourceType?: string, newTargetClass?: string) => {
    const filters = {
      searchQuery: newSearchQuery !== undefined ? newSearchQuery : searchQuery,
      resourceType: newResourceType !== undefined ? newResourceType : resourceType,
      targetClass: newTargetClass !== undefined ? newTargetClass : targetClass,
    }

    console.log("Resource filters changed:", filters)
    onFilterChange?.(filters)
  }

  const clearFilters = () => {
    setSearchQuery("")
    setResourceType("")
    setTargetClass("")
    onFilterChange?.({ searchQuery: "", resourceType: "", targetClass: "" })
    console.log("Resource filters cleared")
  }

  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6">
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search bar */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search resources..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              handleFilterChange(e.target.value, undefined, undefined)
            }}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
          />
        </div>

        {/* Filter toggle button */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition"
        >
          <Filter size={18} />
          <span className="font-medium">Filters</span>
        </button>
      </div>

      {/* Expanded filters */}
      {showFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Resource type filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Resource Type</label>
              <select
                value={resourceType}
                onChange={(e) => {
                  setResourceType(e.target.value)
                  handleFilterChange(undefined, e.target.value, undefined)
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
              >
                <option value="">All Types</option>
                <option value="PDF">PDF Documents</option>
                <option value="Video">Videos</option>
                <option value="Quiz">Quizzes</option>
                <option value="Worksheet">Worksheets</option>
                <option value="Presentation">Presentations</option>
                <option value="Other">Other</option>
              </select>
            </div>

            {/* Target class filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Target Class</label>
              <select
                value={targetClass}
                onChange={(e) => {
                  setTargetClass(e.target.value)
                  handleFilterChange(undefined, undefined, e.target.value)
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
              >
                <option value="">All Classes</option>
                <option value="JSS 1">JSS 1</option>
                <option value="JSS 2">JSS 2</option>
                <option value="JSS 3">JSS 3</option>
                <option value="SS 1">SS 1</option>
                <option value="SS 2">SS 2</option>
                <option value="SS 3">SS 3</option>
              </select>
            </div>

            {/* Clear filters */}
            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-200 transition"
              >
                <X size={18} />
                <span className="font-medium">Clear Filters</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
